import classNames from 'classnames';
import { Chip, ChipProps } from 'primereact/chip';
import { Tooltip } from 'primereact/tooltip';
import { TooltipOptions } from 'primereact/tooltip/tooltipoptions';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { v4 } from 'uuid';

const PulsatingDot = styled.span`
  width: 7px;
  min-width: 7px;
  max-width: 7px;
  height: 7px;
  min-height: 7px;
  max-height: 7px;
  border-radius: 50%;
  background-color: currentColor;
  display: inline-block;
  vertical-align: middle;
  transform: scale(1);
  box-shadow: 0 0 0 currentColor;
  animation: anim-vibrate 1.5s infinite;
  margin-right: var(--xxsmall-padding);

  @keyframes anim-vibrate {
    0% {
      transform: scale(0.85);
      box-shadow: 0 0 0 0 currentColor;
    }

    60% {
      transform: scale(1);
      box-shadow: 0 0 0 0.2rem transparent;
    }

    100% {
      transform: scale(0.85);
      box-shadow: 0 0 0 0 transparent;
    }
  }
`;

const StyledAppChip = styled(Chip)`
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  border-radius: var(--small-border-radius);
  white-space: nowrap;

  &.p-chip-removable .p-chip-text {
    padding-right: 8px;
  }

  &.chip-primary {
    color: var(--green-dark);
    background: var(--green-pale);
  }

  &.chip-secondary {
    color: var(--black-main);
    background: var(--gray-pale);
  }

  &.chip-beige {
    color: var(--black-main);
    background: var(--beige-main);
  }

  &.chip-error {
    color: var(--red-main);
    background: var(--red-pale);
  }

  &.chip-yellow {
    color: var(--yellow-dark);
    background: var(--yellow-main);
  }

  &.chip-orange {
    color: var(--orange-main);
    background: var(--orange-pale);
  }

  &.chip-purple {
    color: var(--white-main);
    background: var(--purple-dark);
  }

  &.chip-purple-outline {
    color: var(--purple-deepest);
    background: transparent;
    border: 1px solid var(--gray-dark);
  }

  &.chip-blue {
    color: var(--blue-darkest);
    background: var(--blue-opacity);
  }

  &.chip-blue-dark {
    color: var(--white-main);
    background: var(--blue-darkest);
  }

  &.chip-red {
    color: var(--red-dark);
    background: var(--red-dark-opacity);
  }

  &.chip-green {
    background-color: color-mix(in srgb, var(--green-color) 10%, transparent);
    color: var(--green-color);
  }

  &.chip-gray-dark {
    color: var(--gray-darker);
    background: transparent;
    border: 1px solid var(--gray-dark);
  }

  &.chip-platinum {
    color: var(--gray-darkest);
    background: var(--platinum-main);
  }

  &[class*='chip-phishing-'] {
    background-color: var(--green-opacity-lighter);
    color: var(--green-color);
  }

  &.chip-sm {
    padding-inline: var(--small-padding);
    height: 28px;
  }

  &.chip-s {
    padding-inline: var(--small-padding);
    height: 22px;
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    font-weight: 500;

    .p-chip-text {
      margin: 0;
      align-self: center;
    }
  }

  &.chip-xs {
    font-size: var(--xxsmall-font-size);
    line-height: var(--xxsmall-line-height);
    padding-inline: var(--small-padding);
    height: 19px;
    min-width: 87px;

    .p-chip-text {
      margin-block: var(--xxxsmall-padding);
      width: 100%;
      text-align: center;
      font-weight: 500;
    }
  }

  &.chip-xs-auto {
    font-size: var(--xxsmall-font-size);
    line-height: var(--xxsmall-line-height);
    padding: 2px 9px;
    min-width: auto;
    height: auto;
    font-weight: 500;

    .p-chip-text {
      margin: 0;
    }
  }

  svg {
    margin-right: var(--xxsmall-padding);
    fill: currentColor;
  }
`;

type AppChipProps = {
  label: string | undefined;
  type:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'yellow'
    | 'orange'
    | 'beige'
    | 'purple'
    | 'purple-outline'
    | 'blue'
    | 'blue-dark'
    | 'red'
    | 'green'
    | 'gray-dark'
    | 'platinum'
    | string;
  size?: 'xs-auto' | 'xs' | 'sm' | 's';
  className?: string;
  pulsate?: boolean;
  icon?: string | ReactNode;
  tooltip?: string | null;
  tooltipOptions?: TooltipOptions;
};

const AppChip: React.FC<
  AppChipProps & ChipProps & React.HTMLAttributes<any>
> = ({
  label,
  type,
  size,
  className,
  pulsate,
  icon,
  tooltip,
  tooltipOptions,
  ...rest
}) => {
  const id = v4();
  const styles = classNames(
    {
      [`chip-${type}`]: type,
      [`chip-${size}`]: size,
    },
    className,
  );

  const wrappedIcon = pulsate ? (
    <PulsatingDot key="app-chip-pulsating-dot" />
  ) : (
    icon
  );

  return (
    <>
      {tooltip && (
        <Tooltip
          target={`#id-${id}`}
          content={tooltip}
          position="top"
          {...tooltipOptions}
        />
      )}
      <StyledAppChip
        id={`id-${id}`}
        label={label}
        className={styles}
        icon={wrappedIcon}
        {...rest}
      />
    </>
  );
};

export default AppChip;
